import { useSpotifyAuth } from './useSpotifyAuth';
import { extractTrackId } from '@/services/qrParser';
import type { SpotifyTrack } from '@/types/spotify';

const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';

export function useSpotifyApi() {
  const { getAccessToken } = useSpotifyAuth();

  /**
   * Ruft Track-Informationen von der Spotify API ab
   * @param uri - Track URI (spotify:track:ID) oder Track-ID
   */
  async function getTrackByUri(uri: string): Promise<SpotifyTrack | null> {
    const token = getAccessToken();
    if (!token) {
      console.error('Kein Access Token verfügbar');
      return null;
    }

    try {
      const trackId = extractTrackId(uri);
      const response = await fetch(`${SPOTIFY_API_BASE}/tracks/${trackId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`API-Fehler: ${response.status}`);
      }

      const track: SpotifyTrack = await response.json();
      return track;
    } catch (error) {
      console.error('Fehler beim Abrufen des Tracks:', error);
      return null;
    }
  }

  /**
   * Ruft mehrere Tracks ab (batch)
   */
  async function getTracksByUris(uris: string[]): Promise<SpotifyTrack[]> {
    const token = getAccessToken();
    if (!token) return [];

    try {
      const ids = uris.map(extractTrackId).join(',');
      const response = await fetch(`${SPOTIFY_API_BASE}/tracks?ids=${ids}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`API-Fehler: ${response.status}`);
      }

      const data = await response.json();
      return data.tracks || [];
    } catch (error) {
      console.error('Fehler beim Abrufen der Tracks:', error);
      return [];
    }
  }

  return {
    getTrackByUri,
    getTracksByUris
  };
}